import { useIntersectionObserver } from '@cian/react-utils';
import * as React from 'react';

import { useIsReplyScrollTarget } from '../../../utils/replyScrollContext';

type TUseScrollToReplyTargetParams = {
  messageId: string;
  messageRef: React.RefObject<HTMLDivElement | null>;
};

type TUseScrollToReplyTargetResult = {
  highlighted: boolean;
  onHighlightEnd: VoidFunction;
};

/**
 * Подскролл к сообщению из реплая и признак того, что его нужно подсветить.
 * Провайдер выставляет цель только когда вся нужная история догружена, поэтому скролл происходит один раз в конце.
 * Если историю догружали страницами — instant, если сообщение уже было в ленте — smooth.
 * Подсветка включается только когда сообщение попало во вьюпорт — иначе анимация отыграет во время длинного smooth-скролла.
 */
export const useScrollToReplyTarget = ({
  messageId,
  messageRef,
}: TUseScrollToReplyTargetParams): TUseScrollToReplyTargetResult => {
  const { isReplyTarget, didLoadHistoryPages, clearReplyTargetMessage } = useIsReplyScrollTarget(messageId);
  const [hasEnteredViewport, setHasEnteredViewport] = React.useState(false);

  const { subscribeState } = useIntersectionObserver({
    elementRef: messageRef,
    initSubscribeAvailable: isReplyTarget,
    deps: [isReplyTarget],
    onInViewport: ({ subscribeState: observerSubscribeState }) => {
      setHasEnteredViewport(true);
      observerSubscribeState.setSubscribeUnavailable();
    },
  });

  React.useEffect(() => {
    if (isReplyTarget) {
      subscribeState.setSubscribeAvailable();
    } else {
      setHasEnteredViewport(false);
      subscribeState.setSubscribeUnavailable();
    }
  }, [isReplyTarget, subscribeState]);

  React.useEffect(() => {
    if (!isReplyTarget) {
      return;
    }

    /** В зависимости от того, грузили ли страницы сообщений, меняем поведение скролла */
    const behavior: ScrollBehavior = didLoadHistoryPages() ? 'instant' : 'smooth';

    const timer = setTimeout(() => {
      messageRef.current?.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });

      /**
       * Instant-скролл синхронный: IntersectionObserver часто успевает отдать
       * первый callback «не во вьюпорте» и не стреляет повторно после прыжка.
       */
      if (behavior === 'instant') {
        setHasEnteredViewport(true);
        subscribeState.setSubscribeUnavailable();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [isReplyTarget, didLoadHistoryPages, messageRef, subscribeState]);

  return { highlighted: isReplyTarget && hasEnteredViewport, onHighlightEnd: clearReplyTargetMessage };
};
